import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { notificationService } from '../services/notificationService';
import RelatedInventory from '../components/RelatedInventory';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';

const { FiDollarSign, FiCar, FiCheckCircle, FiTrendingUp, FiCalendar, FiUpload } = FiIcons;

const TradeIn = () => {
  const [tradeInData, setTradeInData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    mileage: '',
    condition: '',
    accidents: '',
    maintenance: '',
    modifications: '',
    name: '',
    email: '',
    phone: '',
    comments: '',
  });

  const [estimatedValue, setEstimatedValue] = useState(null);

  const handleTradeInSubmit = async (e) => {
    e.preventDefault();
    // Simulate trade-in valuation
    const baseValue = 15000;
    const yearFactor = (2025 - parseInt(tradeInData.year)) * -500;
    const mileageFactor = (parseInt(tradeInData.mileage) / 1000) * -50;
    const conditionMultiplier = {
      'excellent': 1.1,
      'good': 1.0,
      'fair': 0.85,
      'poor': 0.7
    };
    
    const estimated = Math.max(1000, (baseValue + yearFactor + mileageFactor) * (conditionMultiplier[tradeInData.condition] || 1));
    setEstimatedValue(Math.round(estimated));

    // Fire Meta Pixel Lead event
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'trade_in_estimate', content_category: 'form_submission' });
    }
    
    // Send email notification with trade-in details
    try {
      await notificationService.sendTradeInNotification({
        ...tradeInData,
        estimatedValue: Math.round(estimated),
        interestedVehicle: 'Not specified - General Trade-In Inquiry'
      });
      console.log('✅ Trade-in notification sent');
    } catch (emailError) {
      console.error('Failed to send trade-in notification:', emailError);
      // Don't block form submission if email fails
    }
    
    console.log('Trade-in form submitted:', tradeInData);
    // Handle form submission here
  };

  const handleTradeInChange = (e) => {
    setTradeInData({
      ...tradeInData,
      [e.target.name]: e.target.value,
    });
  };

  const benefits = [
    {
      icon: FiDollarSign,
      title: 'Fair Market Value',
      description: 'Get the best possible value for your trade-in vehicle',
    },
    {
      icon: FiCheckCircle,
      title: 'Instant Evaluation',
      description: 'Receive an immediate estimate online with our appraisal tool',
    },
    {
      icon: FiTrendingUp,
      title: 'Tax Benefits',
      description: 'Reduce your sales tax on your next vehicle purchase',
    },
    {
      icon: FiCar,
      title: 'Any Condition',
      description: 'We accept vehicles in any condition, running or not',
    },
  ];

  const makes = [
    'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge',
    'Ford', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jeep', 'Kia', 'Lexus',
    'Lincoln', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan', 'Ram', 'Subaru',
    'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
  ];

  const years = Array.from({ length: 25 }, (_, i) => 2025 - i);

  const process = [
    {
      step: 1,
      title: 'Get Your Estimate',
      description: 'Fill out our online form to get an instant trade-in estimate',
    },
    {
      step: 2,
      title: 'Schedule Inspection',
      description: 'Bring your vehicle for a quick inspection at our dealership',
    },
    {
      step: 3,
      title: 'Receive Final Offer',
      description: 'Get your final trade-in offer based on the inspection',
    },
    {
      step: 4,
      title: 'Complete Transaction',
      description: 'Use your trade-in value toward your next vehicle purchase',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-black text-white"
    >
      <Helmet>
        <title>Trade-In Your Vehicle - Get Instant Appraisal | Paradise-Performance</title>
        <meta name="description" content="Get an instant trade-in estimate for your vehicle. Fair market value, tax benefits for Texas residents, and we accept any condition. Start your free appraisal today." />
              <link rel="canonical" href="https://paradise-performance.com/trade-in" />
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Trade-In"
        title="Trade-In Your Vehicle at Paradise-Performance"
        subtitle="Get an instant estimate and fair market value for your current vehicle in Paradise, TX. Use it toward your next purchase and save on Texas taxes."
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
          <a
            href="#appraisal"
            className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
          >
            Get Instant Estimate
          </a>
          <a
            href="tel:+18173822316"
            className="inline-flex items-center justify-center rounded-xl border border-white/30 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white hover:text-black"
          >
            Call for Appraisal
          </a>
        </div>
      </PageHero>

      {/* Benefits */}
      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Advantage"
            title="Why Trade With Us?"
            subtitle="We offer competitive trade-in values for vehicles in Paradise and make the process simple and transparent for all Texas residents."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
              >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                  <SafeIcon icon={benefit.icon} className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{benefit.title}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-neutral-950 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Process"
            title="How It Works"
            subtitle="Our simple 4-step process makes trading in your vehicle easy and stress-free at our Paradise dealership."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
              >
                <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-lg font-bold text-white">
                  {step.step}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{step.title}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Appraisal Form */}
      <section id="appraisal" className="scroll-mt-24 bg-black py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Instant Appraisal"
            title="Get Your Trade-In Estimate"
            subtitle="Provide some basic information about your vehicle to get an instant estimate."
          />

          <div className="rounded-3xl border border-white/10 bg-white p-6 text-gray-900 shadow-2xl shadow-black/40 sm:p-8 md:p-10">
              <form onSubmit={handleTradeInSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <select
                      name="year"
                      value={tradeInData.year}
                      onChange={handleTradeInChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Year</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Make *
                    </label>
                    <select
                      name="make"
                      value={tradeInData.make}
                      onChange={handleTradeInChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Make</option>
                      {makes.map((make) => (
                        <option key={make} value={make}>
                          {make}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model *
                    </label>
                    <input
                      type="text"
                      name="model"
                      value={tradeInData.model}
                      onChange={handleTradeInChange}
                      className="form-input"
                      placeholder="e.g., Camry"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trim Level
                    </label>
                    <input
                      type="text"
                      name="trim"
                      value={tradeInData.trim}
                      onChange={handleTradeInChange}
                      className="form-input"
                      placeholder="e.g., LE, XLE"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mileage *
                    </label>
                    <input
                      type="number"
                      name="mileage"
                      value={tradeInData.mileage}
                      onChange={handleTradeInChange}
                      className="form-input"
                      placeholder="e.g., 50000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Overall Condition *
                    </label>
                    <select
                      name="condition"
                      value={tradeInData.condition}
                      onChange={handleTradeInChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Condition</option>
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accident History *
                    </label>
                    <select
                      name="accidents"
                      value={tradeInData.accidents}
                      onChange={handleTradeInChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Option</option>
                      <option value="none">No Accidents</option>
                      <option value="minor">Minor Accidents</option>
                      <option value="major">Major Accidents</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maintenance History
                    </label>
                    <select
                      name="maintenance"
                      value={tradeInData.maintenance}
                      onChange={handleTradeInChange}
                      className="form-select"
                    >
                      <option value="">Select Option</option>
                      <option value="excellent">Excellent - Full Records</option>
                      <option value="good">Good - Some Records</option>
                      <option value="average">Average - Basic Maintenance</option>
                      <option value="poor">Poor - Minimal Maintenance</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modifications
                  </label>
                  <textarea
                    name="modifications"
                    value={tradeInData.modifications}
                    onChange={handleTradeInChange}
                    rows={3}
                    className="form-input"
                    placeholder="List any modifications or aftermarket parts..."
                  ></textarea>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={tradeInData.name}
                        onChange={handleTradeInChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={tradeInData.email}
                        onChange={handleTradeInChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={tradeInData.phone}
                        onChange={handleTradeInChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Comments
                  </label>
                  <textarea
                    name="comments"
                    value={tradeInData.comments}
                    onChange={handleTradeInChange}
                    rows={3}
                    className="form-input"
                    placeholder="Any additional information about your vehicle..."
                  ></textarea>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="w-full rounded-xl bg-red-600 px-8 py-4 font-semibold text-white transition-colors hover:bg-red-700 sm:w-auto"
                  >
                    Get My Trade-In Estimate
                  </button>
                </div>
              </form>

              {/* Estimated Value Display */}
              {estimatedValue && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 rounded-2xl border-2 border-red-200 bg-red-50 p-6"
                >
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Estimated Trade-In Value
                    </h3>
                    <div className="text-4xl font-bold text-red-600 mb-4">
                      ${estimatedValue.toLocaleString()}
                    </div>
                    <p className="text-gray-600 mb-4">
                      This is a preliminary estimate. Final value will be determined after vehicle inspection.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <a
                        href="/contact"
                        className="rounded-xl bg-red-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-red-700"
                      >
                        Schedule Inspection
                      </a>
                      <a
                        href="/inventory"
                        className="rounded-xl border border-red-600 px-6 py-3 font-semibold text-red-600 transition-colors hover:bg-red-600 hover:text-white"
                      >
                        Browse Our Inventory
                      </a>
                    </div>
                  </div>
                </motion.div>
              )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-neutral-950 py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-white md:text-4xl">Ready to Trade In Your Vehicle?</h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-400">
            Visit our dealership for a professional appraisal and get the best value for your trade.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
            >
              Schedule Appointment
            </a>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center rounded-xl border border-white/30 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white hover:text-black"
            >
              Call Us Today
            </a>
          </div>
        </div>
      </section>

      {/* Vehicle Inventory Links */}
      <section className="bg-black pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl shadow-black/40 sm:p-8 md:p-10">
            <RelatedInventory limit={4} title="Featured Vehicles For Sale" />
          </div>
        </div>
      </section>
    </motion.div>

  );
};

export default TradeIn;